// Import self-hosted fonts (GDPR compliant)
// latin-ext e obligatoriu pentru romana: ă (U+0103), ș (U+0219), ț (U+021B) nu
// sunt in subsetul latin, deci fara el fiecare cuvant cu diacritice se randeaza
// in doua fonturi. → docs/knowledge/font-diacritics-fallback.md
// Fonturile directiei B "Teren", de pe scoalaecoterrienilor.ecotic.ro.
// Import pe GREUTATE, nu pe subset intreg: subseturile intregi ar aduce 24 de
// @font-face (6 greutati x 2 subseturi x 2 familii) pentru cele 5 chiar folosite.
// Asa sunt 10 - cu 8 mai putine decat cele 18 ale lui Montserrat.
// `latin-ext` e obligatoriu la amandoua: fara el, diacriticele romanesti cad pe
// fontul de sistem (defectul trait cu Montserrat).
import '@fontsource/oswald/latin-500.css';
import '@fontsource/oswald/latin-ext-500.css';
import '@fontsource/oswald/latin-600.css';
import '@fontsource/oswald/latin-ext-600.css';
import '@fontsource/open-sans/latin-400.css';
import '@fontsource/open-sans/latin-ext-400.css';
import '@fontsource/open-sans/latin-600.css';
import '@fontsource/open-sans/latin-ext-600.css';
import '@fontsource/open-sans/latin-700.css';
import '@fontsource/open-sans/latin-ext-700.css';

console.log('🔧 Console logs are working! Environment:', import.meta.env.MODE, '| Drop console:', import.meta.env.VITE_DROP_CONSOLE);

import { IS_PRODUCTION } from "./constants/config";
import { trackException } from "@/telemetry/app-insights";
import { instaleazaReincarcareLaBucataLipsa } from "@/core/version/reincarcare";
import React, { Suspense } from "react";
import { RoutesProvider } from "./providers/routes-provider";
import AuthProviderV2 from "./providers/auth-provider-v2";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ReactDOM from "react-dom/client";
import App from "./app.jsx";
import "./main.css";
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from './core/api/query-client';
import { useRoutesProvider } from "./providers/provider-hooks";
import { createBrowserRouter, Navigate, RouterProvider } from "react-router-dom";
import { lazy, useMemo } from "react";
import AuthenticatedLayout from "./layouts/AuthenticatedLayout.tsx";
import UnauthenticatedLayout from "./layouts/UnauthenticatedLayout.jsx";

// Clean up old service worker registrations to prevent stale cache issues
if ('serviceWorker' in navigator) {
    navigator.serviceWorker.getRegistrations().then((registrations) => {
        registrations.forEach((registration) => {
            registration.unregister()
                .then(() => console.log('✅ Unregistered old service worker'))
                .catch((error) => console.error('❌ Failed to unregister service worker:', error));
        });
    });
}

// Un tab ramas deschis peste un deploy cere bucati de cod care nu mai exista pe server.
instaleazaReincarcareLaBucataLipsa();

// Log build info for debugging (helps track deployment versions)
console.log('🚀 ECOTIC Frontend loaded - BUILD:', import.meta.env.VITE_BUILD_TIME || new Date().toISOString());

const root = ReactDOM.createRoot(document.getElementById("root"));

// Custom error fallback component
const ErrorFallback = ({ error, componentStack, resetError }) => (
    <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        padding: '2rem',
        fontFamily: 'system-ui, -apple-system, sans-serif',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
    }}>
        <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '2rem',
            maxWidth: '600px',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
        }}>
            <h1 style={{ color: '#e53e3e', marginTop: 0 }}>🔴 Oops! Ceva nu a mers bine</h1>
            <p style={{ color: '#4a5568', lineHeight: 1.6 }}>
                Ne pare rău, dar aplicația a întâmpinat o eroare neașteptată.
                Echipa noastră a fost notificată automat și lucrăm la rezolvare.
            </p>
            {!IS_PRODUCTION && (
                <details style={{ marginTop: '1rem', padding: '1rem', background: '#f7fafc', borderRadius: '6px' }}>
                    <summary style={{ cursor: 'pointer', fontWeight: 'bold', color: '#2d3748' }}>
                        Detalii tehnice (doar în development)
                    </summary>
                    <pre style={{
                        marginTop: '1rem',
                        padding: '1rem',
                        background: '#1a202c',
                        color: '#f7fafc',
                        borderRadius: '4px',
                        overflow: 'auto',
                        fontSize: '0.875rem'
                    }}>
                        {error.toString()}{'\n\n'}{componentStack}
                    </pre>
                </details>
            )}
            <div style={{ marginTop: '2rem', display: 'flex', gap: '1rem' }}>
                <button
                    onClick={resetError}
                    style={{
                        padding: '0.75rem 1.5rem',
                        background: '#667eea',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontWeight: 'bold',
                        fontSize: '1rem'
                    }}
                >
                    🔄 Încearcă din nou
                </button>
                <button
                    onClick={() => window.location.href = '/'}
                    style={{
                        padding: '0.75rem 1.5rem',
                        background: 'white',
                        color: '#667eea',
                        border: '2px solid #667eea',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontWeight: 'bold',
                        fontSize: '1rem'
                    }}
                >
                    🏠 Pagina principală
                </button>
            </div>
        </div>
    </div>
);

// RouterWrapper - Creates router from RoutesProvider config and provides router context
const RouterWrapper = () => {
    const { NAV_LINKS_V2 } = useRoutesProvider();

    // Generate route groups by layout (same logic from App.jsx)
    const routesByLayout = useMemo(() => {
        const grouped = {
            public: [],
            authenticated: [],
            open: [],
        };

        Object.entries(NAV_LINKS_V2).forEach(([pageKey, config]) => {
            if (!config.layout || !grouped[config.layout]) return;
            const routeConfig = { pageKey, ...config };

            // If route is marked for lazy loading and has a component reference (not JSX)
            if (config.lazy === true && typeof config.component === 'function') {
                routeConfig.LazyComponent = lazy(() =>
                    Promise.resolve({ default: config.component })
                );
            }

            grouped[config.layout].push(routeConfig);
        });

        return grouped;
    }, [NAV_LINKS_V2]);

    // Helper to render route elements
    const renderRouteElement = (route) => {
        if (route.LazyComponent) return <route.LazyComponent />;
        if (typeof route.component === 'function') return <route.component />;
        return route.component;
    };

    // Create router configuration with createBrowserRouter (required for useBlocker)
    const router = useMemo(
        () =>
            createBrowserRouter([
                {
                    // App is the root element wrapping all routes
                    element: <App />,
                    children: [
                        // ============================================
                        // UNAUTHENTICATED ROUTES (Public)
                        // Auto-generated from NAV_LINKS_V2
                        // ============================================
                        {
                            element: <UnauthenticatedLayout />,
                            children: [
                                { path: "/", element: <Navigate to="/autentificare" replace /> },
                                ...routesByLayout.public.map((route) => ({
                                    path: route.PATH,
                                    element: renderRouteElement(route),
                                })),
                            ],
                        },
                        // ============================================
                        // AUTHENTICATED ROUTES WITH SIDEBAR
                        // Auto-generated from NAV_LINKS_V2
                        // ============================================
                        {
                            element: <AuthenticatedLayout />,
                            children: routesByLayout.authenticated.map((route) => ({
                                path: route.PATH,
                                element: renderRouteElement(route),
                            })),
                        },
                        // ============================================
                        // OPEN ROUTES - fara niciun layout de auth.
                        // Pagina publica a linkului de colaborator: trebuie sa mearga si pentru un
                        // utilizator logat (coordonatorul isi testeaza linkul), deci NU trece prin
                        // UnauthenticatedLayout, care il redirecteaza.
                        // ============================================
                        ...routesByLayout.open.map((route) => ({
                            path: route.PATH,
                            element: renderRouteElement(route),
                        })),
                        // Catch-all: redirect to login
                        { path: "*", element: <Navigate to="/autentificare" replace /> },
                    ],
                },
            ],
            {
                future: {
                    v7_startTransition: true,
                },
            }
        ),
        [routesByLayout]
    );

    return <RouterProvider router={router} />;
};

// React ErrorBoundary class component
class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null, errorInfo: null };
    }

    static getDerivedStateFromError(error) {
        return { hasError: true };
    }

    componentDidCatch(error, errorInfo) {
        this.setState({ error, errorInfo });
        trackException(error, errorInfo);
        console.error('ErrorBoundary caught an error:', error, errorInfo);
    }

    resetError = () => {
        this.setState({ hasError: false, error: null, errorInfo: null });
    }

    render() {
        if (this.state.hasError) {
            return <ErrorFallback
                error={this.state.error}
                componentStack={this.state.errorInfo?.componentStack}
                resetError={this.resetError}
            />;
        }
        return this.props.children;
    }
}

const AppWithProviders = () => (
    <ErrorBoundary>
        <RoutesProvider>
            <RouterWrapper />
        </RoutesProvider>
    </ErrorBoundary>
);

// Lazy load React Query Devtools (dev only)
const ReactQueryDevtools = import.meta.env.DEV
    ? React.lazy(() => import('@tanstack/react-query-devtools').then(mod => ({
        default: mod.ReactQueryDevtools
    })))
    : null;

// Use React Query (migration complete - always enabled)
root.render(
    <QueryClientProvider client={queryClient}>
        <AppWithProviders />
        {/* React Query Devtools - Only in development */}
        {ReactQueryDevtools && (
            <Suspense fallback={null}>
                <ReactQueryDevtools initialIsOpen={false} />
            </Suspense>
        )}
    </QueryClientProvider>
);
